<template>
  <v-text-field
    v-model="value[$i18n.locale]"
    :label="label"
    :rules="rules"
    :hint="hint"
    persistent-hint
  >
    <template v-slot:append>
      <!-- La pastille passe au rouge tant que fr ou en manque : CRUD-Wine
           refusera l'enregistrement, autant le voir avant de cliquer. -->
      <v-dialog transition="dialog-bottom-transition" max-width="640">
        <template v-slot:activator="{ on, attrs }">
          <v-btn v-bind="attrs" v-on="on" icon :color="complete ? 'black' : 'error'">
            <v-icon>mdi-translate</v-icon>
          </v-btn>
        </template>
        <template v-slot:default="dialog">
          <v-card>
            <v-toolbar class="pl-6 text-h6" color="primary" dark>
              {{ label }}
              <v-spacer></v-spacer>
              <v-btn icon @click="dialog.value = false">
                <v-icon color="white">mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-card-text class="pa-9">
              <!-- Une boucle plutot que neuf blocs recopies : ajouter une
                   locale ne demandera qu'une entree dans `locales`. -->
              <v-text-field
                v-for="locale in locales"
                :key="locale"
                class="mx-2"
                v-model="value[locale]"
                :label="locale.toUpperCase() + (required.includes(locale) ? ' *' : '')"
                :error="required.includes(locale) && !value[locale]"
              ></v-text-field>
              <div class="d-flex justify-center">
                <v-btn class="md-raised" depressed color="primary" @click="dialog.value = false">
                  {{ $t('intranet.referential.button.save') }}
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </template>
      </v-dialog>
    </template>
  </v-text-field>
</template>

<script>
/**
 * Un champ traduit : la locale courante en ligne, les neuf langues dans un
 * dialogue (REDMINE-12962).
 *
 * Extrait de `AveineReferentialForm` parce que le contenu editorial de la Home
 * en compte cinq — theme, question, explication, description, etiquettes — et
 * que recopier le dialogue cinq fois aurait ete intenable.
 *
 * `value` est mute en place, sans `v-model` : les traductions sont des objets
 * portes par la ressource editee, et le parent tient deja cette reference.
 */
export default {
  name: 'AveineTranslatedField',
  props: {
    /** L'objet de traductions { fr, en, ... }, mute en place. */
    value: { type: Object, required: true },
    label: { type: String, required: true },
    /**
     * Langues exigees par le back. Par defaut fr et en : c'est la regle posee
     * sur tout le contenu editorial de la Home, appliquee par les validateurs
     * de CRUD-Wine. Un enregistrement sans elles part en 422.
     */
    required: { type: Array, default: () => ['fr', 'en'] }
  },
  data: () => ({
    locales: ['fr', 'en', 'it', 'de', 'es', 'ja', 'ko', 'zh', 'nl']
  }),
  created () {
    // Vue 2 ne rend pas reactive une cle ajoutee apres coup a un objet. Les
    // traductions arrivent en `{}` sur une creation, et le `v-model` ci-dessus
    // y ECRIRAIT des cles invisibles du systeme de reactivite : `complete` ne
    // serait jamais recalcule, la regle de validation resterait fausse, et le
    // bouton d'enregistrement resterait grise pour toujours.
    //
    // On declare donc les neuf locales d'emblee, via $set.
    for (const locale of this.locales) {
      if (!(locale in this.value)) this.$set(this.value, locale, null)
    }
  },
  computed: {
    complete () {
      return this.required.every(locale => !!this.value[locale])
    },
    hint () {
      return this.complete
        ? ''
        : this.$t('intranet.homeContent.validation.translationsRequired', {
          languages: this.required.map(l => l.toUpperCase()).join(', ')
        })
    },
    /**
     * Le champ en ligne edite la locale courante, qui n'est pas forcement une
     * langue exigee : la validation porte donc sur l'ensemble, pas sur lui.
     *
     * La closure ignore l'argument que Vuetify lui passe : elle regarde
     * l'objet entier. Ce qui declenche la reevaluation, c'est que `rules` est
     * un computed dependant de `complete` — il change d'identite des qu'une
     * locale exigee est remplie ou vidée.
     *
     * Cela ne tient QUE parce que `created()` a declare les neuf locales avec
     * $set. Sans elles, `complete` ne serait jamais recalcule et le bouton
     * d'enregistrement resterait grise indefiniment.
     */
    rules () {
      return [() => this.complete || this.hint]
    }
  }
}
</script>
